import { getUuid } from '@sibe/utilities/common';
import type { HsGeometry } from '../../graphic-engine-new/geometry/geometry.interface';
import type { HsMaterial } from '../../graphic-render-new/materials/material.class';
import type { HsGraphicQueue } from '../../graphic-render-new/objects/graphic-queue.class';
import { HsVector3 } from '../../math-new/objects/vector3.class';
import type { HsHypersceneTypes } from '../../math-new/types/hyperscene-types.type';
import { HsObject3D } from '../../engine-new/objects/common/object-3d.class';
import type { HsEmitter } from './emitter.interface';
import type { HsParticle } from './particle.interface';
import type { HsCollider } from './collider.class';
import type { HsForce } from './force.class';
import { HsParticleObject } from './particle-object.class';
import { HsParticleSprite } from './particle-sprite.class';
import { HsSpriteGeometry } from '../../graphic-engine-new/geometry/sprite-geometry.class';
import { HsWindForce } from './wind-force.class';

export class HsParticleSystem extends HsObject3D {
  public size: number = 1;
  public lifeTime: number = 3;
  public spawnRate: number = 500;

  public scattering: number = 0;
  public sizeVariation: number = 0;

  private lastFrameTime: number = performance.now();
  private lastSpawnTime: number = performance.now();

  private particleId: number = 0;

  private readonly particles: Map<number, HsParticleObject | HsParticleSprite> = new Map();

  private queue: HsGraphicQueue | null = null;
  private geometry: HsGeometry | null = null;
  private material: HsMaterial | null = null;
  private emitter: HsEmitter | null = null;
  private forces: HsForce[] = [];
  private colliders: HsCollider[] = [];

  constructor() {
    super(getUuid(), 'Particle System');
  }

  public setQueue(queue: HsGraphicQueue): void {
    this.queue = queue;
  }

  public setGeometry(geometry: HsGeometry): void {
    this.geometry = geometry;
  }

  public setMaterial(material: HsMaterial): void {
    this.material = material;
  }

  public setEmitter(emitter: HsEmitter): void {
    this.emitter = emitter;
  }

  public setForces(forces: HsForce[]): void {
    this.forces = forces;
  }

  public setColliders(colliders: HsCollider[]): void {
    this.colliders = colliders;
  }

  public addParticle(): void {
    if (this.queue === null || this.geometry === null || this.material === null || this.emitter === null) {
      return;
    }
    const center: HsHypersceneTypes.Array3 = this.worldMatrix.getPositionArray();
    this.emitter.setCenter(...center);
    const position: HsVector3 = HsVector3.makeZeroVector();
    this.emitter.getRandomPosition(position);
    const particle: HsParticleObject | HsParticleSprite = this.makeParticleByGeometry(this.geometry, this.material);
    particle.position.copy(position);
    particle.velocity.setRandom().multiplyByScalar(this.scattering);
    particle.worldMatrix.setPosition(position.values);
    const size: number = this.size + this.size * Math.random() * this.sizeVariation;
    particle.size = size;
    if (particle instanceof HsParticleSprite) {
      particle.scale = size;
    }
    particle.worldMatrix.scale([size, size, size]);
    particle.updateBoundingSphere();
    this.particles.set(this.particleId, particle);
    this.particleId++;
    this.queue.addObject(particle);
  }

  public update(): void {
    if (this.queue === null) {
      return;
    }
    const currentTime: number = performance.now();
    const deltaTime: number = currentTime - this.lastFrameTime;
    this.lastFrameTime = currentTime;
    for (let i: number = 0; i < this.forces.length; i++) {
      const force: HsForce = this.forces[i];
      if (force instanceof HsWindForce) {
        force.update();
      }
    }
    for (const [id, particle] of this.particles) {
      if (particle.age / 1000 / this.lifeTime > 0.9) {
        particle.size *= 0.9;
        if (particle instanceof HsParticleSprite) {
          particle.scale = particle.size;
        }
        particle.worldMatrix.scale([particle.size, particle.size, particle.size]);
      }
      if (particle.age / 1000 > this.lifeTime) {
        this.queue.removeObject(particle);
        this.particles.delete(id);
        continue;
      }
      this.applyForces(particle, deltaTime);
      this.applyCollisions(particle);
      particle.update();
      particle.updateBoundingSphere();
      particle.age += deltaTime;
    }
    const spawnDeltaTime: number = currentTime - this.lastSpawnTime;
    const particlesToSpawn: number = Math.min(Math.floor(spawnDeltaTime / (1000 / this.spawnRate)), this.spawnRate);
    if (particlesToSpawn > 0) {
      for (let i: number = 0; i < particlesToSpawn; i++) {
        this.addParticle();
      }
      this.lastSpawnTime = currentTime;
    }
  }

  private makeParticleByGeometry(geometry: HsGeometry, material: HsMaterial): HsParticleObject | HsParticleSprite {
    if (geometry instanceof HsSpriteGeometry) {
      return new HsParticleSprite(getUuid(), this.name, geometry, material);
    }
    return new HsParticleObject(getUuid(), this.name, geometry, material);
  }

  private applyForces(particle: HsParticle, deltaTime: number): void {
    particle.acceleration.set(0, 0, 0);
    for (let i: number = 0; i < this.forces.length; i++) {
      const force: HsForce = this.forces[i];
      force.apply(particle, deltaTime);
    }
  }

  private applyCollisions(particle: HsParticle): void {
    for (let i: number = 0; i < this.colliders.length; i++) {
      const collider: HsCollider = this.colliders[i];
      const d: number = collider.getDistance(particle.position);
      if (d < 0 + particle.size) {
        const n: HsVector3 = HsVector3.makeZeroVector();
        collider.getNormal(particle.position, n);
        const reflection: HsVector3 = HsVector3.subtract(
          particle.velocity,
          n.multiplyByScalar(HsVector3.dot(particle.velocity, n) * 2)
        );
        const offset: HsVector3 = HsVector3.makeCopy(reflection).multiplyByScalar(d - 2);
        particle.position.subtract(offset);
        particle.velocity.copy(reflection).multiplyByScalar(collider.bounciness);
      }
    }
  }
}
